import { useAppState } from '@/hooks/useAppState';
import { getDomain } from '@/utils/getDomain';
import {
  getPasswordResetPageUrl,
  getUserLogInUrl,
  getUserLogOutUrl,
  getUserPageUrl,
  getUserSignUpUrl,
} from '@/utils/getUserAuthUrl';
import { isClient } from '@/utils/isClient';
import { mergeStable } from '@/utils/merge';
import { decodeJwt } from 'jose';
import { AlltIdJwtToken } from 'lib/alltIdMyData';
import { getOcelotConfig } from 'lib/configs/ocelot';
import useCookies from 'lib/hooks/useCookies';
import { CreateAccountPasswordless, createAccountPasswordless } from 'lib/services/alltid';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';

type Urls = {
  logInUrl: string;
  logOutUrl: string;
  signUpUrl: string;
  myPageUrl: string;
};

const shouldUseV2Auth = getOcelotConfig('features.login.v2.enabled');

const getUrls = (state: Urls, returnTo: string) => {
  const params = returnTo
    ? ((shouldUseV2Auth ? { return_to: returnTo } : { return: returnTo }) as Record<string, string>)
    : undefined;
  const alltIdLogOutUrl = !shouldUseV2Auth && isClient() && window.alltId?.getBrandLogoutURL(returnTo);

  const urls = {
    logInUrl: getUserLogInUrl(params),
    logOutUrl: alltIdLogOutUrl || getUserLogOutUrl(params),
    signUpUrl: getUserSignUpUrl(params),
    myPageUrl: getUserPageUrl(params),
    passwordResetUrl: getPasswordResetPageUrl(params),
  };

  return mergeStable(state, urls);
};

const isValidCookie = (cookie: string | undefined) => Boolean(cookie && cookie.length > 0);

export type UserAuthProps = ReturnType<typeof useUserAuth>;

type UseUserAuthOptions = {
  customReturnTo?: string;
};

const useUserAuth = (options?: UseUserAuthOptions) => {
  const pathname = usePathname();
  const [{ login_token, has_session }] = useCookies('login_token', 'has_session');
  const [isUserLoggedIn, setUserLoggedIn] = useState(false);
  const [urls, setReturnTo] = useReducer(getUrls, getUrls({} as Urls, ''));
  const [alltIdReady, setAlltIdReady] = useState(false);
  const [userTags, setUserTags] = useState<string[]>([]);
  const [{ alltIdUserCode, isNewUser }, appStateDispatcher] = useAppState();

  const dispatchAlltIdUserControlCode = useCallback(
    (userCC: string | undefined) => {
      appStateDispatcher({
        alltIdUserCode: userCC,
      });
    },
    [appStateDispatcher],
  );

  const resetAlltIdUserControlCode = useCallback(
    () => dispatchAlltIdUserControlCode(undefined),
    [dispatchAlltIdUserControlCode],
  );

  const dispatchIsNewUser = useCallback(
    (isNewUser: boolean) => {
      appStateDispatcher({
        isNewUser,
      });
    },
    [appStateDispatcher],
  );

  useEffect(() => {
    if (shouldUseV2Auth) return;

    const handleReady = () => setAlltIdReady(true);
    document.addEventListener('alltIdLoggedIn', handleReady);

    return () => document.removeEventListener('alltIdLoggedIn', handleReady);
  }, []);

  useEffect(() => {
    const { customReturnTo } = options || {};
    const siteUrl = getDomain(false);
    const isStartPage = pathname === '/';
    const defaultReturnTo = pathname.startsWith('/mina-sidor')
      ? siteUrl
      : isStartPage
        ? `${siteUrl}/mina-sidor`
        : `${siteUrl}${pathname}`;

    setReturnTo(customReturnTo || defaultReturnTo);
  }, [pathname, options?.customReturnTo, alltIdReady]);

  useEffect(() => {
    if (login_token) {
      const decodedToken = JSON.parse(atob(login_token?.split('.')[1] ?? ''));
      dispatchAlltIdUserControlCode(decodedToken.data.cc);
    }
  }, [dispatchAlltIdUserControlCode, login_token]);

  useEffect(() => {
    if (shouldUseV2Auth) {
      setUserLoggedIn(isValidCookie(has_session));
    } else {
      setUserLoggedIn(isValidCookie(login_token));
    }
  }, [login_token, has_session]);

  useEffect(() => {
    const payload = (login_token ? decodeJwt(login_token) : {}) as AlltIdJwtToken;
    setUserTags(payload?.data?.user?.tags || []);
  }, [login_token]);

  const createPasswordlessAccount = useCallback<CreateAccountPasswordless>(
    async (userData, newsletterSubscription = false) => {
      const { alltIdUserCode, isNewUser } = await createAccountPasswordless(userData, newsletterSubscription);
      dispatchAlltIdUserControlCode(alltIdUserCode);
      dispatchIsNewUser(isNewUser);

      return { alltIdUserCode, isNewUser };
    },
    [dispatchAlltIdUserControlCode, dispatchIsNewUser],
  );

  return useMemo(
    () => ({
      isUserLoggedIn,
      isNewUser,
      alltIdUserCode,
      createPasswordlessAccount,
      userTags,
      resetAlltIdUserControlCode,
      ...urls,
    }),
    [isUserLoggedIn, urls, alltIdUserCode, createPasswordlessAccount, userTags, isNewUser, resetAlltIdUserControlCode],
  );
};

export default useUserAuth;
